import { useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import { Button, Modal, Select, TextInput } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { MetaTags } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_SUPPRESSED_VOE_CONTACTS,
  LIFT_VOE_CONTACT_SUPPRESSION,
  SUPPRESS_VOE_CONTACT,
} from 'src/graphql/voeContacts.graphql'

const inferContactType = (value: string) =>
  value.includes('@') ? 'EMAIL' : 'PHONE'

const AdminVoESuppressedPage = () => {
  const [search, setSearch] = useState('')
  const [applied, setApplied] = useState('')
  const [addOpened, addHandlers] = useDisclosure(false)
  const [newValue, setNewValue] = useState('')
  const [newDotNumber, setNewDotNumber] = useState('')
  const [newType, setNewType] = useState<string | null>(null)

  const { data, loading, refetch } = useQuery(GET_SUPPRESSED_VOE_CONTACTS, {
    variables: { search: applied || null },
    fetchPolicy: 'cache-and-network',
  })

  const [lift, { loading: lifting }] = useMutation(
    LIFT_VOE_CONTACT_SUPPRESSION,
    {
      onCompleted: ({ liftVoEContactSuppression: result }) => {
        toast(`${result.value} can be used again`)
        refetch()
      },
      onError: (error) => toast(error.message, 'error'),
    }
  )

  const [suppress, { loading: suppressing }] = useMutation(
    SUPPRESS_VOE_CONTACT,
    {
      onCompleted: ({ suppressVoEContact: result }) => {
        toast(`${result.value} is now suppressed`)
        closeAdd()
        refetch()
      },
      onError: (error) => toast(error.message, 'error'),
    }
  )

  const closeAdd = () => {
    addHandlers.close()
    setNewValue('')
    setNewDotNumber('')
    setNewType(null)
  }

  const contactType = newType ?? inferContactType(newValue)

  const PAGE_SIZE = 20
  const matched = data?.suppressedVoEContacts ?? []
  const rows = matched.slice(0, PAGE_SIZE)
  const hasMore = matched.length > PAGE_SIZE

  return (
    <div className="flex h-full flex-col gap-5 pb-16 pr-6">
      <MetaTags
        title="Suppressed contacts"
        description="Addresses blocked from employer verification outreach"
      />

      <div className="flex flex-col gap-1">
        <div className="text-lg font-semibold text-doubleNickel-gray-900">
          Suppressed
        </div>
        <div className="max-w-3xl text-sm text-doubleNickel-gray-600">
          Email addresses, phone numbers and fax numbers we have been asked
          never to use again. Once something is here it cannot come back — not
          from a driver&apos;s application, not filled in automatically, and no
          email or call we send will reach it. A block covers every client
          unless it names a DOT number, in which case it only applies to that
          one carrier. Lift it to allow the contact point again.
        </div>
        <div className="max-w-3xl text-sm text-doubleNickel-gray-600">
          Adding one here only stops future use — contacts and verifications
          that already have it are left unchanged. To take a contact out of use
          and clear the records holding it, use Remove on the Contacts page.
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <TextInput
          id="suppressed-search"
          label="Filter by address or number"
          placeholder="name@carrier.com"
          className="min-w-[20rem]"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          onKeyDown={(event) =>
            event.key === 'Enter' && setApplied(search.trim())
          }
        />
        <Button onClick={() => setApplied(search.trim())} loading={loading}>
          Filter
        </Button>
        <Button variant="outline" onClick={addHandlers.open}>
          Add suppression
        </Button>
        {applied && (
          <Button
            variant="subtle"
            onClick={() => {
              setSearch('')
              setApplied('')
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {hasMore && (
        <div className="text-sm text-doubleNickel-gray-500">
          Showing the {PAGE_SIZE} most recent. Filter above to find a specific
          one.
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-sm text-doubleNickel-gray-500">
          {applied
            ? `Nothing suppressed matches “${applied}”.`
            : 'Nothing is suppressed.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-doubleNickel-gray-200">
          <table className="w-full text-left text-xs">
            <thead className="bg-doubleNickel-gray-50 text-doubleNickel-gray-600">
              <tr>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Value</th>
                <th className="px-3 py-2 font-medium">Applies to</th>
                <th className="px-3 py-2 font-medium">Suppressed by</th>
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.suppressedVoEContactId}
                  className="border-t border-doubleNickel-gray-200"
                >
                  <td className="px-3 py-2">{row.contactType}</td>
                  <td className="px-3 py-2 font-mono">{row.value}</td>
                  <td className="px-3 py-2">
                    {row.dotNumber ? `DOT ${row.dotNumber}` : 'Everywhere'}
                  </td>
                  <td className="px-3 py-2">{row.suppressedBy ?? 'Contact'}</td>
                  <td className="px-3 py-2">
                    {dayjs(row.createdAt).format('MMM D, YYYY')}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="compact-xs"
                      variant="outline"
                      loading={lifting}
                      onClick={() =>
                        lift({
                          variables: {
                            contactType: row.contactType,
                            value: row.value,
                            dotNumber: row.dotNumber ?? null,
                          },
                        })
                      }
                    >
                      Lift
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal
        opened={addOpened}
        onClose={closeAdd}
        title="Suppress a contact point"
        centered
      >
        <div className="flex flex-col gap-4 text-sm">
          <TextInput
            id="new-suppression-value"
            label="Value"
            placeholder="name@carrier.com or (555) 123-4567"
            value={newValue}
            onChange={(event) => setNewValue(event.currentTarget.value)}
          />
          <Select
            id="new-suppression-type"
            label="Type"
            data={[
              { value: 'EMAIL', label: 'Email' },
              { value: 'PHONE', label: 'Phone' },
              { value: 'FAX', label: 'Fax' },
            ]}
            value={contactType}
            onChange={setNewType}
            allowDeselect={false}
          />
          <TextInput
            id="new-suppression-dot"
            label="DOT number"
            placeholder="Leave blank to block it everywhere"
            description="Blocks it for that one carrier only. Leave blank and it is blocked for every client."
            value={newDotNumber}
            onChange={(event) => setNewDotNumber(event.currentTarget.value)}
          />
          <div className="rounded-md border border-doubleNickel-warning-200 bg-doubleNickel-warning-25 p-3 text-doubleNickel-gray-700">
            This only stops future use. Contacts and verifications that already
            have it are left as they are — use Remove on the Contacts page for
            those.
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="subtle" onClick={closeAdd}>
              Cancel
            </Button>
            <Button
              loading={suppressing}
              disabled={!newValue.trim()}
              onClick={() =>
                suppress({
                  variables: {
                    contactType,
                    value: newValue.trim(),
                    dotNumber: newDotNumber.trim() || null,
                  },
                })
              }
            >
              Suppress
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default AdminVoESuppressedPage
